<script setup>
  import {computed, inject, nextTick, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import {router, useForm} from "@inertiajs/vue3";
  import {MedButton} from "@pharma4u/medicheck-components";
  import {pdfRouter} from "@utils/pdfRouter/pdfRouter.js";
  import {isAxiosError} from "axios";

  defineOptions({
    name: "ComponentBmpShowDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
  });

  const privacy = inject("$privacy");

  const refDialog = ref(null);
  const refConfirmationDialog = ref(null);

  // form for deletion
  const form = useForm({
    mp: null,
  });

  const mp = ref(null);

  const error = ref(null);

  const valid = computed(() => {
    return mp.value !== null;
  });

  function open() {
    // because the following router.reload() only reloads the current
    // url and does not allow to append a get parameter, we have to
    // inject the withMp parameter manually to include this
    // field in our patient ressource.
    let url = new URL(globalThis.location.href);
    url.searchParams.set("withMp", 1);
    history.pushState({}, "", url);
    router.reload({
      only: ["patient", "patients"],
      preserveState: true,
      preserveScroll: true,
      onSuccess: (page) => {
        nextTick(() => {
          try {
            const encryptedMp =
              page.props?.patient?.mp ?? page.props?.patients?.data?.find((p) => p.id === props.patient.id)?.mp;

            if (!encryptedMp) {
              throw new Error("Der gespeicherte BMP konnte nicht geladen werden.");
            }

            privacy
              .decryptValue(encryptedMp)
              .then((decrypted) => {
                mp.value = decrypted;
              })
              .catch((error) => {
                throw new Error("Fehler beim Entschlüsseln des gespeicherten BMPs.", error);
              });
          } catch (error_) {
            error.value = error_.message;
          }
        });
      },
      onError: () => {
        error.value = "Es trat ein Fehler beim Laden des bereits gespeicherten BMPs auf.";
      },
    });

    refDialog.value.open();
  }

  function close() {
    mp.value = null;
    error.value = null;

    // remove get param from current url
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
    refDialog.value?.close();
  }

  function openBmp() {
    pdfRouter.setSerializedMp(mp.value, {
      onSuccess: (res) => {
        if (res.data.url) {
          location.href = res.data.url + "/" + props.patient.id;
        } else {
          throw new Error(
            "Bei Übertragen der Daten an den BMP-Manager entsprach die Antwort nicht dem erwarteten Format.",
          );
        }
      },
      onError: (error_) => {
        if (isAxiosError(error_)) {
          error.value = error_?.response?.data?.message ?? error_.message;
        } else if (error_ instanceof Error) {
          error.value = error_.message;
        } else {
          error.value = "Bei der Kommunikation mit dem BMP-Manager trat ein Fehler auf.";
        }
      },
    });
  }

  function deleteBmp() {
    form.patch(route("patients.update", {patient: props.patient.id}), {
      onSuccess: () => {
        close();
      },
    });
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog ref="refDialog" dialog-title="Gespeicherten BMP öffnen" :test-id="getTestId('bmp-show')">
    <template #content>
      <ComponentAlert v-if="error" type="error">
        <strong class="font-semibold">Fehler:</strong>
        {{ error }} Bitte probieren Sie es erneut.
      </ComponentAlert>

      <p>Die aktuelle Version des BMP wurde gespeichert am {{ patient.mp_date }}.</p>

      <ComponentConfirmationDialog
        ref="refConfirmationDialog"
        title="Sind Sie sicher?"
        content="Der aktuell gespeicherte BMP wird gelöscht."
        :test-id="getTestId('bmp-show-dialog-delete-confirm')"
        @confirmed="deleteBmp()"
      />
    </template>

    <template #actions>
      <MedButton
        variant="primary"
        :disabled="!valid || form.processing"
        :data-test="getTestId('bmp-show-dialog-open-button')"
        @click="openBmp()"
      >
        Öffnen
      </MedButton>

      <MedButton :data-test="getTestId('bmp-show-dialog-close-button')" @click="close()">Abbrechen</MedButton>

      <MedButton
        flat
        variant="primary"
        :disabled="form.processing"
        :data-test="getTestId('bmp-show-dialog-delete-button')"
        @click="$refs.refConfirmationDialog.open()"
      >
        Löschen
      </MedButton>
    </template>
  </ComponentDialog>
</template>
